import { ChecklistMetricStatus } from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import { VoiceAirMetricDefinition } from '../voice-air-metrics.types';
import { getBusinessSettingsQueryOptions } from '../voice-air-handoff.queries';
import { getParsedErrorMessage } from 'redux/axios';
import { WEAVE_APP_URL } from 'apis/sso/sso-url-config';

const DEFAULT_START = '09:00:00';
const DEFAULT_END = '17:00:00';

export const phoneOfficeHoursMetric: VoiceAirMetricDefinition = {
  id: 'phone_office_hours',
  name: 'Phone/Office Hours',
  isRequired: false,
  criteria: [
    {
      description: 'Ensure Phone and Office Hours match scheduling parameters.',
      whereToFix: {
        label: 'Bridgeline Business Preferences',
        url: `${WEAVE_APP_URL}/home/dashboard#settings/ai-configuration/ai-receptionist/business-preferences/business-profile`,
      },
      evaluate: async (ctx) => {
        try {
          const resp = await ctx.queryClient.fetchQuery(
            getBusinessSettingsQueryOptions(ctx.locationId, ctx.orgId)
          );

          const openDays = resp?.businessSettings?.value?.businessOpenDays || {};
          const workingHours = resp?.businessSettings?.value?.businessWorkingHours || {};

          const days = [
            'MONDAY',
            'TUESDAY',
            'WEDNESDAY',
            'THURSDAY',
            'FRIDAY',
            'SATURDAY',
            'SUNDAY',
          ];

          let hasAtLeastOneOpenDay = false;
          let hasValidWorkingHours = false;
          let allOpenDaysAreDefault = true;

          for (const day of days) {
            const isOpen = !!openDays[day];
            if (isOpen) {
              hasAtLeastOneOpenDay = true;
              const range = workingHours[day];
              const start = range?.start?.trim() || '';
              const end = range?.end?.trim() || '';

              if (start && end) {
                hasValidWorkingHours = true;
                const isDefault = start === DEFAULT_START && end === DEFAULT_END;
                if (!isDefault) {
                  allOpenDaysAreDefault = false;
                }
              }
            }
          }

          // Rationale: Blocked if zero usable open days/hours are configured
          if (!hasAtLeastOneOpenDay || !hasValidWorkingHours) {
            return {
              status: ChecklistMetricStatus.BLOCKED,
              reason: 'office_hours_missing',
              message:
                'No office hours are configured. At least one day must have valid open hours for the AI Receptionist to operate.',
            };
          }

          // Rationale: Warning if user has only default hours and hasn't customized
          if (allOpenDaysAreDefault) {
            return {
              status: ChecklistMetricStatus.WARNING,
              reason: 'office_hours_possibly_default',
              message:
                'Office hours appear to use default values (9:00 AM – 5:00 PM for all open days). Please review and confirm they are correct.',
            };
          }

          // Passed
          return {
            status: ChecklistMetricStatus.PASSED,
            reason: 'office_hours_configured',
            message: 'Office hours are configured and appear intentionally set.',
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'office_config_fetch_failed',
            message:
              getParsedErrorMessage(err) ||
              'Failed to retrieve Office Hours. Please configure them in AI Preferences.',
          };
        }
      },
    },
  ],
};
